import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Trash2, TrendingUp } from "lucide-react";
import FeedbackFooter from "@/components/FeedbackFooter";

const moodEmojis = ["😞", "😔", "😐", "🙂", "😊"];
const moodLabels = ["Rough", "Low", "Okay", "Good", "Great"];

export default function Moods() {
    const [moods, setMoods] = useState([]);
    const [selected, setSelected] = useState(3);
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        const { data } = await api.get("/moods");
        setMoods(data.moods || []);
    };

    useEffect(() => { load(); }, []);

    const submit = async () => {
        setLoading(true);
        try {
            await api.post("/moods", { mood: selected, note, emoji: moodEmojis[selected - 1] });
            setNote("");
            toast.success("Check-in saved.");
            await load();
        } catch {
            toast.error("Could not save");
        } finally {
            setLoading(false);
        }
    };

    const remove = async (id) => {
        await api.delete(`/moods/${id}`);
        setMoods(prev => prev.filter(m => m.id !== id));
    };

    const chartData = [...moods].reverse().slice(-30).map(m => ({
        date: new Date(m.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        mood: m.mood,
    }));

    return (
        <div className="max-w-5xl mx-auto p-6 sm:p-10 lg:p-14">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-10">
                <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3 font-medium">Mood tracker</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-[800] tracking-tight">
                    A gentle noticing. <span className="font-[300] italic">No judgment.</span>
                </h1>
            </motion.div>

            {/* Log new */}
            <div className="bg-card rounded-3xl p-8 border border-border shadow-soft mb-8">
                <h2 className="font-heading text-xl font-[700] mb-6 tracking-tight">How are you feeling right now?</h2>
                <div className="grid grid-cols-5 gap-3 mb-6">
                    {moodEmojis.map((e, i) => (
                        <button
                            key={i}
                            onClick={() => setSelected(i + 1)}
                            data-testid={`mood-btn-${i + 1}`}
                            className={`flex flex-col items-center gap-2 p-4 rounded-2xl border transition-transform duration-300 ${
                                selected === i + 1
                                    ? "bg-primary/15 border-primary -translate-y-1 shadow-soft"
                                    : "bg-accent/40 border-border hover:-translate-y-1"
                            }`}
                        >
                            <span className="text-3xl sm:text-4xl">{e}</span>
                            <span className="text-xs font-medium text-muted-foreground">{moodLabels[i]}</span>
                        </button>
                    ))}
                </div>
                <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Anything you'd like to note? (optional)"
                    className="mb-6 rounded-2xl border-border bg-background"
                    data-testid="mood-note-input"
                />
                <Button onClick={submit} disabled={loading} className="rounded-full h-12 px-8 font-medium shadow-soft" data-testid="mood-save-btn">
                    {loading ? "Saving…" : "Save check-in"}
                </Button>
            </div>

            {/* Chart */}
            {chartData.length >= 2 && (
                <div className="bg-card rounded-3xl p-8 border border-border shadow-soft mb-8" data-testid="mood-chart">
                    <div className="flex items-center gap-2 mb-6">
                        <TrendingUp className="w-5 h-5 text-primary" strokeWidth={1.5} />
                        <h2 className="font-heading text-xl font-[700] tracking-tight">Your last 30 check-ins</h2>
                    </div>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(35 20% 88%)" />
                                <XAxis dataKey="date" stroke="hsl(20 10% 50%)" fontSize={11} />
                                <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} stroke="hsl(20 10% 50%)" fontSize={11} />
                                <Tooltip contentStyle={{ background: "hsl(0 0% 100%)", border: "1px solid hsl(35 20% 85%)", borderRadius: 12 }} />
                                <Line type="monotone" dataKey="mood" stroke="hsl(18 70% 60%)" strokeWidth={3} dot={{ r: 4, fill: "hsl(18 70% 60%)" }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* History */}
            <div className="bg-card rounded-3xl p-8 border border-border shadow-soft" data-testid="mood-history">
                <h2 className="font-heading text-xl font-[700] mb-6 tracking-tight">History</h2>
                {moods.length === 0 ? (
                    <p className="text-muted-foreground">No check-ins yet. Your history will live here.</p>
                ) : (
                    <div className="space-y-3">
                        {moods.map(m => (
                            <div key={m.id} className="flex items-start gap-4 p-4 rounded-2xl border border-border bg-accent/20 group">
                                <span className="text-3xl">{m.emoji || moodEmojis[m.mood - 1]}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 mb-1">
                                        <span className="font-medium">{moodLabels[m.mood - 1]}</span>
                                        <span className="text-xs text-muted-foreground">
                                            {new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                                        </span>
                                    </div>
                                    {m.note && <p className="text-sm text-muted-foreground leading-relaxed">{m.note}</p>}
                                </div>
                                <button onClick={() => remove(m.id)} className="opacity-0 group-hover:opacity-100 p-2 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors duration-200" data-testid={`mood-delete-${m.id}`}>
                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <FeedbackFooter />
        </div>
    );
}
